import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { Badge as BadgeData } from '@/lib/types';

const badgeVariants = cva(
  'inline-flex items-center gap-1 whitespace-nowrap rounded-full border font-medium leading-none',
  {
    variants: {
      tone: {
        neutral: 'border-line bg-surface-muted text-ink-2',
        primary: 'border-transparent bg-primary-soft text-primary',
        success: 'border-transparent bg-positive-soft text-positive',
        warning: 'border-transparent bg-warn-soft text-warn',
        danger: 'border-transparent bg-danger-soft text-danger',
        info: 'border-transparent bg-info-soft text-info',
        violet: 'border-transparent bg-violet-soft text-violet',
        outline: 'border-line-strong bg-transparent text-ink-2',
      },
      size: {
        xs: 'px-1.5 py-0.5 text-[10px]',
        sm: 'px-2 py-0.5 text-[11px]',
        md: 'px-2.5 py-1 text-xs',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'sm' },
  },
);

export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>['tone']>;

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  dot?: boolean;
}

export function Badge({ className, tone, size, dot, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ tone, size }), className)} {...props}>
      {dot ? <span className="size-1.5 rounded-full bg-current" aria-hidden /> : null}
      {children}
    </span>
  );
}

/**
 * Maps the CSS class names the Django context builders emit onto badge tones.
 * The server returns e.g. `{label: 'Live', cls: 'wer-badge--danger'}`; keeping
 * the mapping here means the API contract does not have to change.
 */
const CLASS_TO_TONE: Array<[RegExp, BadgeTone]> = [
  [/danger|error|critical|fail|fraud|urgent/i, 'danger'],
  [/b-wait|warn|caution|pending|review/i, 'warning'],
  [/b-end|success|good|positive|ok|complete|resolved/i, 'success'],
  [/b-in|b-taylor|info|inbound|note/i, 'info'],
  [/violet|purple|ai/i, 'violet'],
  [/b-out|primary|brand|accent|outbound/i, 'primary'],
  [/b-vm|muted|neutral|idle/i, 'neutral'],
];

export function toneFromServer(
  badge: Pick<BadgeData, 'cls' | 'css' | 'tone'> | null | undefined,
): BadgeTone {
  if (!badge) return 'neutral';
  const explicit = (badge.tone ?? '').toLowerCase();
  if (explicit) {
    const known: Record<string, BadgeTone> = {
      danger: 'danger',
      error: 'danger',
      warning: 'warning',
      warn: 'warning',
      success: 'success',
      positive: 'success',
      info: 'info',
      violet: 'violet',
      primary: 'primary',
      neutral: 'neutral',
      muted: 'neutral',
    };
    if (known[explicit]) return known[explicit];
  }
  // Conference builders emit `css`; other payloads use `cls`.
  const cls = badge.cls || badge.css || '';
  for (const [pattern, tone] of CLASS_TO_TONE) {
    if (pattern.test(cls)) return tone;
  }
  return 'neutral';
}

/**
 * Tone from a bare CSS class string.
 *
 * Some payloads (booking revenue chips, for one) carry only the class name the
 * Django template would have applied, not a full badge dict.
 */
export function toneFromClass(cls: string | null | undefined): BadgeTone {
  return toneFromServer({ cls: cls ?? '' });
}

/**
 * Tone from a bare tone name.
 *
 * The queue and operations context builders return `*_tone` strings ("danger",
 * "warn", "positive", "muted") that were CSS-class suffixes in the templates.
 */
export function toneFromName(tone: string | null | undefined): BadgeTone {
  return toneFromServer({ tone: tone ?? '', cls: '' });
}

/** Renders a badge dict straight off the API. */
export function ServerBadge({
  badge,
  size,
  className,
  dot,
}: {
  badge: BadgeData | null | undefined;
  size?: BadgeProps['size'];
  className?: string;
  dot?: boolean;
}) {
  if (!badge?.label) return null;
  return (
    <Badge
      tone={toneFromServer(badge)}
      size={size}
      className={className}
      dot={dot}
      title={badge.title || badge.tooltip}
    >
      {badge.label}
    </Badge>
  );
}

/** A removable filter chip, as used above the conference list. */
export function FilterChip({
  label,
  onRemove,
  className,
}: {
  label: string;
  onRemove?: () => void;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-line bg-surface-2 py-1 pl-2.5 pr-1 text-[11px] text-ink-2',
        className,
      )}
    >
      {label}
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="rounded-full p-0.5 text-ink-3 transition-colors hover:bg-surface-muted hover:text-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          aria-label={`Remove filter ${label}`}
        >
          <X className="size-3" />
        </button>
      ) : null}
    </span>
  );
}

export { badgeVariants };
